<template>
    <div class="modal-container">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhGlobe :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>{{ isEditing ? 'Edit' : 'Add' }} Domain</h2>
                    <p v-if="isPrimary">Primary domain for your company</p>
                    <p v-else>Secondary domain for email matching</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>Domain Name</label>
                    <PrimeInputText
                        v-model="formData.domain"
                        placeholder="example.com"
                        class="w-full"
                    />
                    <p class="help-text">Enter the domain without http:// or www.</p>
                </div>
            </div>

            <div class="modal-footer">
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton :label="isEditing ? 'Update' : 'Add'" type="submit" :loading="isLoading" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhGlobe } from '@phosphor-icons/vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.UPDATE_BUSINESS_DOMAIN);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const formData = reactive({
    domain: '',
});

const companyId = computed(() => dialogRef.value?.data?.companyId);
const domain = computed(() => dialogRef.value?.data?.domain);
const isPrimary = computed(() => dialogRef.value?.data?.isPrimary || false);
const isEditing = computed(() => !!domain.value?.id);

onMounted(() => {
    if (domain.value) {
        formData.domain = domain.value.name || domain.value.domain || '';
    }
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (!formData.domain) {
        showError('Error', 'Domain is required');
        return;
    }

    // Remove any protocol or www prefix
    let cleanDomain = formData.domain
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0];

    isLoading.value = true;
    try {
        const payload = {
            id: domain.value?.id,
            domain: cleanDomain,
            is_primary: isPrimary.value,
        };

        await businessService.saveDomain(companyId.value, payload);
        showSuccess('Success', `Domain ${isEditing.value ? 'updated' : 'added'}`);
        dialogRef.value.close({ updated: true });
    } catch (error: any) {
        showError('Error', error.message || 'Failed to save domain');
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';

.modal-body {
    .form-row {
        .help-text {
            @apply text-xs text-zinc-400 mt-1;
        }
    }
}
</style>
